import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/analytics';
import { Layout } from '../components/layout/Layout';
import { usePodcastFeeds } from '../hooks';
import { Button, Card, Spinner, SectionHeader, StatusBadge } from '../components/common';
import PodcastFeedDetail from '../components/feed/PodcastFeedDetail';

export function FeedDetailPage() {
  const { feedId } = useParams<{ feedId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { feeds, loading: feedsLoading } = usePodcastFeeds();

  const isDefaultFeed = feedId === 'default';

  // Get the current feed
  const currentFeed = isDefaultFeed
    ? null
    : feeds.find((f) => f.id === feedId);

  // Extract base URL for RSS feed endpoint
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const baseUrl = apiUrl.replace(/\/api\/?$/, '') || window.location.origin;

  // For default feed, show all completed editions
  // For curated feeds, we'd need to filter based on what's added to that specific feed
  const feedUrl = isDefaultFeed && user?.rssFeedToken
    ? `${baseUrl}/feed/${user.rssFeedToken}`
    : '';

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      trackEvent('feed_url_copied', { surface: 'feed_detail' });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isDefaultFeed && !feedsLoading && !currentFeed) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto animate-fade-in">
          <div className="text-center py-12">
            <h1 className="font-display text-2xl font-medium text-foreground mb-2">
              Feed not found
            </h1>
            <p className="font-body text-muted-foreground mb-6">
              This feed doesn't exist or has been deleted.
            </p>
            <Link to="/feeds">
              <Button variant="primary">Back to feeds</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const isLoading = feedsLoading || (isDefaultFeed ? false : !currentFeed);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/feeds')}
            className="font-body text-sm text-primary hover:underline mb-4"
          >
            ← Back to feeds
          </button>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 mb-8">
                <SectionHeader
                  eyebrow={`${isDefaultFeed ? 'personal' : 'custom'} feed`}
                  title={isDefaultFeed ? 'Default feed' : currentFeed?.name}
                  description={
                    isDefaultFeed
                      ? 'Your personal podcast feed. Subscribe in any podcast app.'
                      : currentFeed?.description ?? undefined
                  }
                  className="mb-0 flex-1"
                />
                {!isDefaultFeed && currentFeed && (
                  <StatusBadge
                    tone={currentFeed.published ? 'ok' : 'off'}
                    className="shrink-0"
                  >
                    {currentFeed.published ? 'published' : 'draft'}
                  </StatusBadge>
                )}
              </div>

              {/* Feed URL for Default Feed */}
              {isDefaultFeed && (
                <Card className="mb-8">
                  <h2 className="font-display text-lg font-medium text-foreground mb-4">
                    Your personal feed URL
                  </h2>
                  <p className="font-body text-muted-foreground mb-6 leading-relaxed">
                    Copy this URL and add it to any podcast app that supports custom feeds
                    — Apple Podcasts, Pocket Casts, Overcast. New conversions appear as episodes
                    automatically.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
                        Standard URL
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={feedUrl || 'Loading...'}
                          className="flex-1 px-3 py-2 bg-muted/50 border border-border rounded-sm font-mono text-sm text-muted-foreground"
                        />
                        <Button
                          onClick={handleCopy}
                          disabled={!feedUrl}
                          variant={copied ? 'primary' : 'secondary'}
                        >
                          {copied ? (
                            <>
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Copied
                            </>
                          ) : (
                            'Copy URL'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 font-body text-sm text-muted-foreground flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Keep this URL private. Anyone with this link can listen to your episodes.
                  </p>
                </Card>
              )}

              {/* Editions */}
              {!isDefaultFeed && currentFeed && (
                <Card className="mt-8">
                  <h2 className="font-display text-lg font-medium text-foreground mb-6">
                    Manage episodes
                  </h2>
                  <PodcastFeedDetail
                    feedId={currentFeed.id}
                    onChange={() => {}}
                  />
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
